import React, { useEffect, useState, useRef } from "react";
import { Box, Divider, TableCell, Tooltip } from "@mui/material";
import CommonCalculator from "../../../builders/calculator/common";
import PdfGenerator from "./Pdf/ServiceIncomePdf";
import {
  formatAmount,
  formatHistoryAmount,
  isAutoload
} from "../../../utils/hooks/dataHandler";
import ScheduleHeader from "../../App/ScheduleHeader";
import ScheduleNotes from "../ScheduleNotes";
import CommonTable, { ReadOnlyCell } from "../../utils/CommonTable";
import {
  TextFieldForName,
  TextFieldForViewOnly
} from "../../utils/TextField/TextField";
import { CalculatorTextField } from "../../utils/TextField/NumericTextField";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import SectionBox from "../utils/SectionBox";
import ScheduleContainer from "../utils/ScheduleContainer";
import CalculationSummary from "../utils/CalculationSummary";
import ShareOfExpenses from "../utils/ShareOfExpenses";

const ServiceIncomeSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true
}) => {
  const [schName, setSchName] = useState("Service Income");
  const [taxAdjustment, setTaxAdjustment] = useState("");
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const [otherIncomeRows, setOtherIncomeRows] = useState([]);
  const [expensesRows, setExpensesRows] = useState([]);
  const [totalOtherIncome, setTotalOtherIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [netOtherIncome, setNetOtherIncome] = useState(0);

  const [shareOfExpenses, setShareOfExpenses] = useState(null);

  const getTableColumns = (handleChange, setRows) => [
    {
      key: "description",
      label: "Description",
      headerAlign: "left",
      render: (row, index) => (
        <>
          {!canEditSchedule ? (
            <ReadOnlyCell
              value={row.description}
              width='100%'
              textAlign='left'
            />
          ) : (
            <TextFieldForName
              value={row.description}
              onChange={handleChange}
              disabled={isAutoload(row.status)}
              reference={{
                index: index,
                field: "description"
              }}
            />
          )}
        </>
      )
    },
    {
      key: "amount",
      label: "Amount",
      headerAlign: "right",
      colAlign: "right",
      render: (row, index) => (
        <>
          {!canEditSchedule ? (
            <ReadOnlyCell
              value={formatAmount(row.amount)}
              width='100%'
              textAlign='right'
            />
          ) : (
            <Tooltip
              title={
                row?.historyAmount &&
                row.historyAmount.length > 0 &&
                formatHistoryAmount(row.historyAmount)
              }
              placement='top'
            >
              <Box display='inline-block' sx={{ width: "100%" }}>
                <CalculatorTextField
                  value={row?.historyAmount || []}
                  onChange={handleChange}
                  reference={{
                    index: index,
                    field: "amount"
                  }}
                  fullWidth={true}
                  disabled={isAutoload(row.status)}
                />
              </Box>
            </Tooltip>
          )}
        </>
      )
    },
    {
      key: "reference",
      label: "Reference",
      headerAlign: "center",
      render: (row, index) => (
        <TextFieldForName
          value={row?.reference}
          onChange={handleChange}
          reference={{
            index: index,
            field: "reference"
          }}
          canEdit={canEditSchedule}
          viewOnlyAlign='center'
        />
      )
    },
    {
      key: "source",
      label: "Sch",
      headerAlign: "center",
      render: (row) => <TextFieldForViewOnly value={row.source?.sch_no || ""} />
    },
    canEditSchedule
      ? {
          key: "action",
          label: "Action",
          headerAlign: "center",
          render: (row, index) => (
            <IconButtonWithTooltip
              icon={DeleteOutlineIcon}
              tooltipTitle='Delete Row'
              onClick={() => removeRow(setRows, index)}
              color='error'
              size='small'
              disabled={isAutoload(row.status)}
            />
          )
        }
      : []
  ];

  const getShareOfExpensesAmount = (shareOfExpensesData = shareOfExpenses) => {
    return company.companyType === "investment"
      ? shareOfExpensesData?.amount || 0
      : 0;
  };

  const calculateTotals = (
    expensesData,
    incomeData,
    shareOfExpensesData = shareOfExpenses
  ) => {
    const c_totalExpenses = CommonCalculator.arraySum(expensesData);
    const c_totalOtherIncome = CommonCalculator.arraySum(incomeData);
    const netIncome =
      c_totalOtherIncome -
      c_totalExpenses -
      getShareOfExpensesAmount(shareOfExpensesData);

    setTotalOtherIncome(c_totalOtherIncome);
    setTotalExpenses(c_totalExpenses);
    setNetOtherIncome(netIncome);
  };

  const handleRowChange = (value, { index, field, rowType }) => {
    const isExpense = rowType === "expense";
    const rows = isExpense ? expensesRows : otherIncomeRows;
    const setRows = isExpense ? setExpensesRows : setOtherIncomeRows;
    const otherRows = isExpense ? otherIncomeRows : expensesRows;

    const updatedRows = [...rows];
    if (field === "amount") {
      updatedRows[index]["historyAmount"] = value;
      updatedRows[index][field] = value.reduce((sum, num) => sum + num, 0);
      calculateTotals(
        isExpense ? updatedRows : otherRows,
        isExpense ? otherRows : updatedRows
      );
    } else {
      updatedRows[index][field] = value;
    }
    setRows(updatedRows);
  };

  const handleOtherIncomeChange = (value, params) => {
    handleRowChange(value, { ...params, rowType: "income" });
  };

  const handleExpenseChange = (value, params) => {
    handleRowChange(value, { ...params, rowType: "expense" });
  };

  const handleSave = () => {
    schedule.name = schName;
    schedule.taxAdjustment = taxAdjustment;
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;

    let finalTotalExpenses = totalExpenses;
    if (company.companyType === "investment") {
      finalTotalExpenses = finalTotalExpenses + getShareOfExpensesAmount();
    }

    saveSchedule(schedule, {
      otherIncomeRows: otherIncomeRows,
      expensesRows: expensesRows,
      totalAdjustment: netOtherIncome,
      totalRevenue: totalOtherIncome,
      totalExpenses: finalTotalExpenses,
      doc: JSON.stringify({
        taxAdjustment,
        otherIncomeRows,
        expensesRows,
        totalOtherIncome,
        totalExpenses: finalTotalExpenses,
        netOtherIncome,
        notes,
        reviewNotes
      })
    });
  };

  useEffect(() => {
    //init
    if (schedule) {
      setSchName(schedule.name);
      setTaxAdjustment(schedule.taxAdjustment);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      setExpensesRows(
        scheduleDetails.expensesRows.map((row) => {
          if (!row.historyAmount || row.historyAmount.length <= 0) {
            row.historyAmount = [row.amount];
          }
          return row;
        })
      );
      setOtherIncomeRows(
        scheduleDetails.otherIncomeRows.map((row) => {
          if (!row.historyAmount || row.historyAmount.length <= 0) {
            row.historyAmount = [row.amount];
          }
          return row;
        })
      );

      const nextShareOfExpenses = scheduleDetails.shareOfExpenses || null;
      setShareOfExpenses(nextShareOfExpenses);

      //calculation
      calculateTotals(
        scheduleDetails.expensesRows,
        scheduleDetails.otherIncomeRows,
        nextShareOfExpenses
      );
    }
  }, []);

  const addRow = (setRows) => {
    setRows((prevRows) => [
      ...prevRows,
      {
        description: "",
        amount: "",
        reference: ""
      }
    ]);
  };

  const removeRow = (setRows, index) => {
    setRows((prevRows) => prevRows.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          companyType: company.companyType,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          currency: company.currency,
          handleSave,
          deleteSchedule,
          canEditSchedule
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          otherIncomeRows={otherIncomeRows}
          expensesRows={expensesRows}
          totalOtherIncome={totalOtherIncome}
          totalExpenses={totalExpenses}
          netIncome={netOtherIncome}
          shareOfExpenses={shareOfExpenses}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schName}
        taxAdjustment={taxAdjustment}
        allowedToEditSchName={canEditSchedule}
        allowedToAdjustTax={canEditSchedule}
        taxAdjustOptions={["", "Added Back"]}
        updateHeading={(newTitle) => {
          schedule.name = newTitle;
          setSchName(newTitle);
        }}
        updateAdjustment={(newAdjustment) => {
          schedule.taxAdjustment = newAdjustment;
          setTaxAdjustment(newAdjustment);
        }}
        canEditSchedule={canEditSchedule}
      >
        {(fullScreenMode) => (
          <>
            {/* Other Rental Income */}
            <SectionBox
              title='Service Income'
              onAdd={() => addRow(setOtherIncomeRows)}
              canEdit={canEditSchedule}
            >
              <CommonTable
                rows={otherIncomeRows}
                columns={getTableColumns(
                  handleOtherIncomeChange,
                  setOtherIncomeRows
                )}
              >
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  Total Service Income
                </TableCell>
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  {formatAmount(totalOtherIncome)}
                </TableCell>
                <TableCell colSpan={3} />
              </CommonTable>
            </SectionBox>

            <Divider sx={{ marginY: 4 }} />
            <SectionBox
              title='Service Expenses'
              onAdd={() => addRow(setExpensesRows)}
              canEdit={canEditSchedule}
            >
              <CommonTable
                rows={expensesRows}
                columns={getTableColumns(handleExpenseChange, setExpensesRows)}
              >
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  Total Expenses
                </TableCell>
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  {formatAmount(totalExpenses)}
                </TableCell>
                <TableCell colSpan={3} />
              </CommonTable>
            </SectionBox>

            {shareOfExpenses && (
              <ShareOfExpenses
                companyType={company.companyType}
                shareOfExpenses={shareOfExpenses}
              />
            )}
          </>
        )}
      </ScheduleContainer>

      <Divider sx={{ marginY: 4 }} />

      <Box>
        <CalculationSummary
          title='Final Income Calculation'
          subtitle='Review the complete calculation breakdown'
          steps={[
            {
              section: "Service Income",
              amount: formatAmount(totalOtherIncome),
              tooltip:
                "Total income from service income. This includes all service income."
            },
            {
              section: "Service Expenses",
              amount: `(${formatAmount(totalExpenses)})`,
              tooltip:
                "Total expenses directly related to generating the service income. These expenses are deducted from the service income."
            },
            ...(company.companyType === "investment" && shareOfExpenses
              ? [
                  {
                    section: "Share of Expenses",
                    amount: formatAmount(-shareOfExpenses?.amount || 0),
                    tooltip: `Share of expenses from ${
                      shareOfExpenses?.source ?? ""
                    }.`
                  }
                ]
              : [])
          ]}
          total={{
            title: "Net Service Income",
            amount: formatAmount(netOtherIncome),
            tooltip:
              "Formula: Service Income - Service Expenses. This represents the net amount of service income after deducting related expenses."
          }}
        />
      </Box>

      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />
    </Box>
  );
};

export default ServiceIncomeSchedule;
